import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users, KeyRound, RefreshCw, Copy, ShieldCheck, Lock, UserPlus, Building2, Pencil, Merge, Trash2 } from "lucide-react";

const ROLE_LABEL = {
  owner: "Owner", manager: "Manager", fo_cs: "Front Office", cs: "Customer Service", crm: "CRM",
  doctor: "Dokter", therapist: "Therapist", super_admin: "Super Admin",
};
const ROLE_COLOR = {
  owner: "bg-foreground text-background", manager: "bg-indigo-100 text-indigo-800",
  fo_cs: "bg-sky-100 text-sky-800", cs: "bg-emerald-100 text-emerald-800", crm: "bg-yellow-100 text-yellow-800",
  doctor: "bg-emerald-100 text-emerald-800",
  therapist: "bg-pink-100 text-pink-800", super_admin: "bg-red-100 text-red-800",
};

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Change-password dialog
  const [changeTarget, setChangeTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset (generated) result dialog
  const [resetResult, setResetResult] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Branches of this clinic (for staff isolation assignment)
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;

  // Assign-branches dialog
  const [branchTarget, setBranchTarget] = useState(null);
  const [selBranches, setSelBranches] = useState([]);
  const openBranches = (u) => { setBranchTarget(u); setSelBranches(u.branch_ids || []); };
  const toggleSel = (id) => setSelBranches((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const saveBranches = async () => {
    setBusy(true);
    try {
      await api.patch(`/auth/users/${branchTarget.id}/branches`, { branch_ids: selBranches });
      toast.success("Akses cabang diperbarui");
      setBranchTarget(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };

  // Add-user dialog
  const isSuper = me?.role === "super_admin";
  const [addOpen, setAddOpen] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "fo_cs", clinic_id: "", branch_ids: [] });
  useEffect(() => {
    if (isSuper) api.get("/admin/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, [isSuper]);
  const openAdd = () => {
    setForm({ name: "", email: "", password: "", role: "fo_cs", clinic_id: clinics[0]?.id || "", branch_ids: [] });
    setAddOpen(true);
  };
  const toggleFormBranch = (id) => setForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id] }));
  const submitAdd = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Nama, email, dan password wajib diisi"); return; }
    if (isSuper && !form.clinic_id) { toast.error("Pilih clinic tujuan"); return; }
    setBusy(true);
    try {
      const payload = { name: form.name, email: form.email, password: form.password, role: form.role, branch_ids: form.branch_ids };
      if (isSuper) payload.clinic_id = form.clinic_id;
      await api.post("/auth/register", payload);
      toast.success(`User ${form.name} berhasil ditambahkan`);
      setAddOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menambah user"); }
    finally { setBusy(false); }
  };

  const openChange = (u) => { setChangeTarget(u); setNewPassword(""); };

  // Edit-user dialog (nama, email, role, cabang)
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "fo_cs", branch_ids: [] });
  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name || "", email: u.email || "", role: u.role, branch_ids: u.branch_ids || [] });
  };
  const toggleEditBranch = (id) => setEditForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id] }));
  const submitEdit = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) { toast.error("Nama & email wajib diisi"); return; }
    setBusy(true);
    try {
      await api.patch(`/auth/users/${editTarget.id}`, {
        name: editForm.name.trim(), email: editForm.email.trim(), role: editForm.role, branch_ids: editForm.branch_ids,
      });
      toast.success(`Data ${editForm.name} diperbarui & tersinkron ke profil`);
      setEditTarget(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  // Dedupe staff profiles
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const runDedupe = async () => {
    if (!window.confirm("Gabungkan & hapus profil dokter/therapist yang namanya duplikat dalam clinic ini? Transaksi terkait akan otomatis dialihkan ke profil tunggal.")) return;
    setDedupeBusy(true);
    try {
      const { data } = await api.post("/admin/staff/dedupe");
      const d = data.doctors, t = data.therapists;
      toast.success(`Selesai — Dokter: ${d.duplicates_removed} duplikat dihapus, Therapist: ${t.duplicates_removed} duplikat dihapus`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal membersihkan duplikat"); }
    finally { setDedupeBusy(false); }
  };

  const submitChange = async () => {
    if (!newPassword) { toast.error("Masukkan password baru"); return; }
    setBusy(true);
    try {
      await api.post(`/auth/users/${changeTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password ${changeTarget.name || changeTarget.email} berhasil diubah`);
      setChangeTarget(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const doReset = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/users/${u.id}/reset-password`, {});
      setConfirmReset(null);
      setResetResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const copyTemp = () => {
    navigator.clipboard?.writeText(resetResult?.temp_password || "");
    toast.success("Password sementara disalin");
  };

  const doDelete = async (u) => {
    setBusy(true);
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`Staf ${u.name || u.email} dihapus`);
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Manajemen User</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Kelola akun staf clinic Anda. Reset atau ubah password staf bila diperlukan.
            {isSuper && " Sebagai Super Admin, Anda dapat menambah user ke clinic mana pun."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="dedupe-staff-btn" onClick={runDedupe} disabled={dedupeBusy}>
            <Merge className="mr-1.5 h-4 w-4" /> {dedupeBusy ? "Membersihkan..." : "Bersihkan Duplikat"}
          </Button>
          <Button data-testid="add-user-btn" onClick={openAdd}>
            <UserPlus className="mr-1.5 h-4 w-4" /> Tambah User
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users className="h-4 w-4" /> {users.length} akun staf
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Cabang</TableHead>
                <TableHead>2FA</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                const locked = u.role === "super_admin";
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="text-sm font-medium">
                      {u.name || "—"} {isSelf && <Badge variant="outline" className="ml-1 text-[10px]">Anda</Badge>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${ROLE_COLOR[u.role] || ""}`}>{ROLE_LABEL[u.role] || u.role}</Badge>
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`user-branches-${u.id}`}>
                      {u.role === "owner" || locked ? (
                        <span className="text-muted-foreground">Semua cabang</span>
                      ) : (u.branch_ids && u.branch_ids.length > 0) ? (
                        <span>{u.branch_ids.map(branchName).join(", ")}</span>
                      ) : (
                        <span className="text-amber-600">Semua (belum diatur)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.two_factor_enabled
                        ? <Badge variant="outline" className="text-xs text-emerald-700"><ShieldCheck className="mr-1 h-3 w-3" /> Aktif</Badge>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {isSelf || locked ? (
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Lock className="h-3 w-3" /> {locked ? "Terkunci" : "Pakai menu Keamanan"}</span>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)}>
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                          </Button>
                          {u.role !== "owner" && (
                            <Button size="sm" variant="outline" data-testid={`assign-branch-${u.id}`} onClick={() => openBranches(u)}>
                              <Building2 className="mr-1.5 h-3.5 w-3.5" /> Cabang
                            </Button>
                          )}
                          <Button size="sm" variant="outline" data-testid={`change-pw-${u.id}`} onClick={() => openChange(u)}>
                            <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Ubah Password
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`reset-pw-${u.id}`} onClick={() => setConfirmReset(u)}>
                            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reset
                          </Button>
                          <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-user-${u.id}`} onClick={() => setConfirmDelete(u)}>
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Hapus
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && users.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">Belum ada staf.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-user-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Staf</DialogTitle>
            <DialogDescription>Ubah nama, email, role, dan penempatan cabang. Perubahan otomatis tersinkron ke profil Dokter/Therapist.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nama</Label>
              <Input data-testid="edit-user-name" className="mt-1" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="edit-user-email" type="email" className="mt-1" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["owner", "manager", "fo_cs", "cs", "crm", "doctor", "therapist"].map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(editForm.role === "doctor" || editForm.role === "therapist") && (
                <p className="mt-1 text-xs text-emerald-600">Profil {ROLE_LABEL[editForm.role]} akan dibuat/di-update otomatis untuk POS & komisi.</p>
              )}
            </div>
            {editForm.role !== "owner" && branches.length > 0 && (
              <div>
                <Label>Penempatan Cabang</Label>
                <p className="text-xs text-muted-foreground mb-1.5">Pilih cabang. Kosongkan = semua cabang.</p>
                <div className="flex flex-wrap gap-1.5">
                  {branches.map((b) => {
                    const on = editForm.branch_ids.includes(b.id);
                    return (
                      <button type="button" key={b.id} data-testid={`edit-user-branch-${b.id}`} onClick={() => toggleEditBranch(b.id)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-secondary"}`}>
                        {b.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Batal</Button>
            <Button data-testid="submit-edit-user" onClick={submitEdit} disabled={busy}>{busy ? "Menyimpan..." : "Simpan Perubahan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Tambah User Baru</DialogTitle>
            <DialogDescription>Buat akun staf baru{isSuper ? " dan pilih clinic tujuannya." : "."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {isSuper && (
              <div>
                <Label>Clinic Tujuan</Label>
                <Select value={form.clinic_id} onValueChange={(v) => setForm({ ...form, clinic_id: v })}>
                  <SelectTrigger data-testid="add-user-clinic" className="mt-1"><SelectValue placeholder="Pilih clinic" /></SelectTrigger>
                  <SelectContent>
                    {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Nama</Label>
              <Input data-testid="add-user-name" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="add-user-email" type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@clinic.id" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="add-user-password" type="text" className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 karakter, 1 huruf besar, 1 angka" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="add-user-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["owner", "manager", "fo_cs", "cs", "crm", "doctor", "therapist"].map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.role !== "owner" && branches.length > 0 && (
              <div>
                <Label>Akses Cabang</Label>
                <p className="text-xs text-muted-foreground mb-1.5">Pilih cabang yang boleh diakses staf ini. Kosongkan = semua cabang.</p>
                <div className="flex flex-wrap gap-1.5">
                  {branches.map((b) => {
                    const on = form.branch_ids.includes(b.id);
                    return (
                      <button type="button" key={b.id} data-testid={`add-user-branch-${b.id}`} onClick={() => toggleFormBranch(b.id)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-secondary"}`}>
                        {b.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button data-testid="submit-add-user" onClick={submitAdd} disabled={busy}>{busy ? "Menyimpan..." : "Tambah User"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign branches dialog */}
      <Dialog open={!!branchTarget} onOpenChange={(o) => !o && setBranchTarget(null)}>
        <DialogContent data-testid="assign-branch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><Building2 className="h-5 w-5" /> Akses Cabang</DialogTitle>
            <DialogDescription>Tentukan cabang yang dapat diakses oleh <b>{branchTarget?.name || branchTarget?.email}</b>. Kosongkan untuk memberi akses semua cabang.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5">
            {branches.length === 0 && <p className="text-sm text-muted-foreground">Belum ada cabang. Minta Super Admin membuat cabang terlebih dahulu.</p>}
            {branches.map((b) => {
              const on = selBranches.includes(b.id);
              return (
                <button type="button" key={b.id} data-testid={`assign-branch-opt-${b.id}`} onClick={() => toggleSel(b.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-secondary"}`}>
                  {b.name}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBranchTarget(null)}>Batal</Button>
            <Button data-testid="save-branches-btn" onClick={saveBranches} disabled={busy}>{busy ? "Menyimpan..." : "Simpan Akses"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!changeTarget} onOpenChange={(o) => !o && setChangeTarget(null)}>
        <DialogContent data-testid="change-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Ubah Password</DialogTitle>
            <DialogDescription>Tetapkan password baru untuk <b>{changeTarget?.name || changeTarget?.email}</b>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Password Baru</Label>
            <Input type="text" data-testid="new-password-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 karakter, 1 huruf besar, 1 angka" />
            <p className="text-xs text-muted-foreground">Syarat: minimal 8 karakter, mengandung huruf besar & angka.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeTarget(null)}>Batal</Button>
            <Button data-testid="submit-change-pw" onClick={submitChange} disabled={busy}>{busy ? "Menyimpan..." : "Simpan Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm reset dialog */}
      <Dialog open={!!confirmReset} onOpenChange={(o) => !o && setConfirmReset(null)}>
        <DialogContent data-testid="confirm-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Reset Password?</DialogTitle>
            <DialogDescription>
              Sistem akan membuat password sementara yang kuat untuk <b>{confirmReset?.name || confirmReset?.email}</b>. Password lama tidak berlaku lagi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReset(null)}>Batal</Button>
            <Button data-testid="confirm-reset-btn" onClick={() => doReset(confirmReset)} disabled={busy}>{busy ? "Memproses..." : "Ya, Reset"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset result dialog (shows generated temp password) */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Password Sementara Dibuat</DialogTitle>
            <DialogDescription>Bagikan password ini ke <b>{resetResult?.name || resetResult?.email}</b>. Mintalah staf segera login & menggantinya.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-4">
            <code className="font-mono text-lg tracking-wider" data-testid="temp-password-value">{resetResult?.temp_password}</code>
            <Button size="sm" variant="outline" onClick={copyTemp} data-testid="copy-temp-password"><Copy className="mr-1.5 h-4 w-4" /> Salin</Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} data-testid="reset-result-done">Selesai</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirm delete dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-rose-700">Hapus Staf?</DialogTitle>
            <DialogDescription>
              Akun <b>{confirmDelete?.name || confirmDelete?.email}</b> ({ROLE_LABEL[confirmDelete?.role] || confirmDelete?.role}) akan dihapus permanen & tidak bisa login lagi. Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Batal</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-btn" onClick={() => doDelete(confirmDelete)} disabled={busy}>{busy ? "Menghapus..." : "Ya, Hapus"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
